import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import type { EditShippingPolicyModalProps, ExpressShippingFieldErrors, FormData } from './types';
import type { ExpressShippingPolicy } from '../../../../infrastructure/ShippingClient';
import ExpressShippingFields from './ExpressShippingFields';
import { getDefaultExpressShipping, supportsExpressShipping, validateExpressShipping } from './expressShipping';

const EditShippingPolicyModal: React.FC<EditShippingPolicyModalProps> = ({
  show,
  onHide,
  onSubmit,
  policy,
  isLoading,
}) => {
  const [shippingDuration, setShippingDuration] = useState({
    minDay: 5,
    maxDay: 10,
  });
  const [expressShipping, setExpressShipping] = useState<ExpressShippingPolicy | undefined>();
  const [errors, setErrors] = useState<{ minDay?: string; maxDay?: string }>({});
  const [expressErrors, setExpressErrors] = useState<ExpressShippingFieldErrors>({});

  // Initialize form data when policy changes
  useEffect(() => {
    if (policy) {
      setShippingDuration({
        minDay: policy.deliveryDuration.minDays,
        maxDay: policy.deliveryDuration.maxDays,
      });
      setExpressShipping(
        policy.expressShipping ??
          (supportsExpressShipping(policy.countryCode) ? getDefaultExpressShipping(policy.countryCode) : undefined),
      );
      setExpressErrors({});
    }
  }, [policy]);

  const handleShippingDurationChange = (field: 'minDay' | 'maxDay', value: number) => {
    setShippingDuration((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { minDay?: string; maxDay?: string } = {};

    if (shippingDuration.minDay < 1) {
      newErrors.minDay = 'Minimum days must be at least 1';
    }

    if (shippingDuration.maxDay < 1) {
      newErrors.maxDay = 'Maximum days must be at least 1';
    }

    if (shippingDuration.minDay > shippingDuration.maxDay) {
      newErrors.maxDay = 'Maximum days must be greater than or equal to minimum days';
    }

    const shouldValidateExpress = expressShipping?.enabled || policy?.expressShipping !== undefined;
    const newExpressErrors = shouldValidateExpress && expressShipping ? validateExpressShipping(expressShipping) : {};
    setErrors(newErrors);
    setExpressErrors(newExpressErrors);
    return Object.keys(newErrors).length === 0 && Object.keys(newExpressErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !policy) {
      return;
    }

    // Create FormData with only required fields for API
    const formData: FormData = {
      countryCode: policy.countryCode,
      shippingDuration,
      ...(expressShipping && (expressShipping.enabled || policy.expressShipping !== undefined)
        ? { expressShipping }
        : {}),
    };

    onSubmit(formData);
  };

  const handleClose = () => {
    setErrors({});
    setExpressErrors({});
    onHide();
  };

  if (!policy) {
    return null;
  }

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Shipping Policy - {policy.countryCode}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Read-only information */}
          <Alert variant="secondary">
            <strong>Policy Information (Read-only):</strong>
            <ul className="mb-0 mt-2">
              <li>
                <strong>Country:</strong> {policy.countryCode}
              </li>
              <li>
                <strong>Shipping Cost:</strong> {formatCurrency(policy.shippingCost)}
              </li>
              <li>
                <strong>Free Shipping Threshold:</strong>{' '}
                {policy.freeShippingThreshold ? formatCurrency(policy.freeShippingThreshold) : 'No free shipping'}
              </li>
              <li>
                <strong>Status:</strong> {policy.enabled ? 'Active' : 'Inactive'}
              </li>
            </ul>
          </Alert>

          {/* Editable delivery duration */}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="edit-shipping-policy-min-days">
                <Form.Label>Minimum Delivery Days *</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={shippingDuration.minDay}
                  onChange={(e) => handleShippingDurationChange('minDay', parseInt(e.target.value) || 1)}
                  isInvalid={Boolean(errors.minDay)}
                />
                <Form.Control.Feedback type="invalid">{errors.minDay}</Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3" controlId="edit-shipping-policy-max-days">
                <Form.Label>Maximum Delivery Days *</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={shippingDuration.maxDay}
                  onChange={(e) => handleShippingDurationChange('maxDay', parseInt(e.target.value) || 1)}
                  isInvalid={Boolean(errors.maxDay)}
                />
                <Form.Control.Feedback type="invalid">{errors.maxDay}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {expressShipping && (
            <ExpressShippingFields
              countryCode={policy.countryCode}
              value={expressShipping}
              errors={expressErrors}
              onChange={(value) => {
                setExpressShipping(value);
                setExpressErrors({});
              }}
            />
          )}

          <Alert variant="info">
            <strong>Current Delivery Time:</strong>{' '}
            {shippingDuration.minDay === shippingDuration.maxDay
              ? `${shippingDuration.minDay} ${shippingDuration.minDay === 1 ? 'day' : 'days'}`
              : `${shippingDuration.minDay}-${shippingDuration.maxDay} days`}
          </Alert>

          {(Object.keys(errors).length > 0 || Object.keys(expressErrors).length > 0) && (
            <Alert variant="danger">Please fix the errors above before submitting.</Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? 'Updating...' : 'Update Policy'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditShippingPolicyModal;
